import { useState, useEffect }                        from '@wordpress/element';
import { __, sprintf }                               from '@wordpress/i18n';
import { classNames }                               from '../../../utils/helpers';
import { isInWishlist, toggleWishlist, onWishlistChange } from '../../../utils/wishlist';

export default function ProductCard( { product, settings } ) {
	const [ activeImage, setActiveImage ] = useState( 0 );
	const [ adding, setAdding ]           = useState( false );
	const [ added, setAdded ]             = useState( false );
	const [ wishlisted, setWishlisted ]   = useState( () => isInWishlist( product.id ) );

	const isEditor = !! window.mwsbData?.isEditor;

	useEffect( () => {
		return onWishlistChange( ( ids ) => setWishlisted( ids.includes( product.id ) ) );
	}, [ product.id ] );

	const {
		showImage, showImageSlider, showSaleBadge, showStockBadge,
		saleBadgeBgColor, saleBadgeRadius, saleBadgePadding,
		showTitle, showRating, showPrice, showDescription,
		showAddToCart, showQuickView, showWishlist, layoutPreset,
		showCategories, categoryPosition, categoryFontSize, categoryColor, categoryFontWeight, categoryPadding,
		imageRadius, imageHeight, imageObjectFit,
		cardRadius, cardBorderWidth, cardBorderColor, cardBodyPadding,
		titleFontSize, titleFontSizeTablet, titleFontSizeMobile,
		titleColor, titleFontWeight, titleLink, titleHoverColor,
		priceFontSize, priceFontSizeTablet, priceFontSizeMobile,
		priceFontWeight, priceColor,
		btnBgColor, btnTextColor,
		btnFontSize, btnFontSizeTablet, btnFontSizeMobile,
		btnBorderRadius, btnBorderWidth, btnBorderColor,
		btnHoverBgColor, btnHoverTextColor,
		cartBtnStyle, cartIconPosition, cartIconSize, cartIconBtnSize, cartIconOffset,
		qvBtnBgColor, qvBtnTextColor, qvBtnFontSize, qvBtnBorderRadius, qvBtnBorderWidth,
		qvBtnBorderColor, qvBtnPadding, qvBtnHoverBgColor, qvBtnHoverTextColor, qvBtnHoverBorderColor,
		wlBtnBgColor, wlBtnIconColor, wlBtnBorderWidth, wlBtnBorderColor, wlBtnBorderRadius,
		wlBtnPadding, wlBtnHoverBgColor, wlBtnHoverIconColor, wlBtnHoverBorderColor,
		wlBtnActiveColor, wlBtnActiveBgColor,
		wlBtnPosition, wlBtnShowOnHover,
		contentAlign,
	} = settings;

	const images     = product.images || [];
	const hasGallery = images.length > 1;
	const outOfStock = product.stock_status === 'outofstock';
	const wlOnImage  = showWishlist && wlBtnPosition && wlBtnPosition !== 'card' && ( showImage || showImageSlider ) && images.length > 0;
	const cartAsIcon  = cartBtnStyle === 'icon';
	const cartOnImage = cartAsIcon && cartIconPosition === 'image-bottom-right' && ( showImage || showImageSlider ) && images.length > 0;
	const cartOnCard  = cartAsIcon && cartIconPosition === 'card-bottom-right';

	const cardStyle = {
		borderRadius: cardRadius != null ? `${ cardRadius }px` : undefined,
		border:       cardBorderWidth > 0
			? `${ cardBorderWidth }px solid ${ cardBorderColor || '#e5e7eb' }`
			: 'none',
		'--mwsb-card-title-fs':   titleFontSize        ? `${ titleFontSize }px`        : undefined,
		'--mwsb-card-title-fs-t': titleFontSizeTablet  ? `${ titleFontSizeTablet }px`  : undefined,
		'--mwsb-card-title-fs-m': titleFontSizeMobile  ? `${ titleFontSizeMobile }px`  : undefined,
		'--mwsb-card-price-fs':   priceFontSize        ? `${ priceFontSize }px`        : undefined,
		'--mwsb-card-price-fs-t': priceFontSizeTablet  ? `${ priceFontSizeTablet }px`  : undefined,
		'--mwsb-card-price-fs-m': priceFontSizeMobile  ? `${ priceFontSizeMobile }px`  : undefined,
		'--mwsb-card-btn-fs':     btnFontSize          ? `${ btnFontSize }px`          : undefined,
		'--mwsb-card-btn-fs-t':   btnFontSizeTablet    ? `${ btnFontSizeTablet }px`    : undefined,
		'--mwsb-card-btn-fs-m':   btnFontSizeMobile    ? `${ btnFontSizeMobile }px`    : undefined,
	};

	const imageStyle = {
		height:       imageHeight > 0 ? `${ imageHeight }px` : undefined,
		borderRadius: imageRadius != null ? `${ imageRadius }px` : undefined,
	};

	const imgStyle = {
		objectFit: imageHeight > 0 ? ( imageObjectFit || 'cover' ) : undefined,
	};

	const titleStyle = {
		fontWeight:            titleFontWeight || undefined,
		'--mwsb-title-color': titleColor      || undefined,
		'--mwsb-title-hover': titleHoverColor || undefined,
	};

	const categoryStyle = {
		'--mwsb-cat-fs':    categoryFontSize   ? `${ categoryFontSize }px` : undefined,
		'--mwsb-cat-color': categoryColor      || undefined,
		'--mwsb-cat-fw':    categoryFontWeight || undefined,
		...(categoryPadding ? {
			paddingTop:    categoryPadding.top,
			paddingRight:  categoryPadding.right,
			paddingBottom: categoryPadding.bottom,
			paddingLeft:   categoryPadding.left,
		} : {}),
	};

	const priceStyle = {
		fontWeight: priceFontWeight || undefined,
		color:      priceColor      || undefined,
	};

	const alignJustify = contentAlign === 'center' ? 'center' : contentAlign === 'right' ? 'flex-end' : 'flex-start';

	const bodyStyle = {
		...(cardBodyPadding ? {
			paddingTop:    cardBodyPadding.top,
			paddingRight:  cardBodyPadding.right,
			paddingBottom: cardBodyPadding.bottom,
			paddingLeft:   cardBodyPadding.left,
		} : {}),
		textAlign: contentAlign || 'left',
	};

	const btnStyle = {
		borderRadius: btnBorderRadius != null ? `${ btnBorderRadius }px` : undefined,
		border:       btnBorderWidth > 0
			? `${ btnBorderWidth }px solid ${ btnBorderColor || 'currentColor' }`
			: undefined,
		'--mwsb-btn-bg':         btnBgColor        || undefined,
		'--mwsb-btn-text':       btnTextColor      || undefined,
		'--mwsb-btn-hover-bg':   btnHoverBgColor   || undefined,
		'--mwsb-btn-hover-text': btnHoverTextColor || undefined,
		'--mwsb-cart-icon-btn-size': cartAsIcon && cartIconBtnSize ? `${ cartIconBtnSize }px` : undefined,
		'--mwsb-cart-icon-offset':   cartAsIcon && cartIconOffset != null ? `${ cartIconOffset }px` : undefined,
	};

	const toPx = ( v ) => {
		if ( ! v ) return undefined;
		return /^\d+(\.\d+)?$/.test( String( v ) ) ? `${ v }px` : String( v );
	};

	const saleBadgeStyle = {
		background:    saleBadgeBgColor  || undefined,
		borderRadius:  saleBadgeRadius != null ? `${ saleBadgeRadius }px` : undefined,
		paddingTop:    toPx( saleBadgePadding?.top ),
		paddingRight:  toPx( saleBadgePadding?.right ),
		paddingBottom: toPx( saleBadgePadding?.bottom ),
		paddingLeft:   toPx( saleBadgePadding?.left ),
	};

	const wlBtnStyle = {
		'--mwsb-wl-card-btn-bg':           wlBtnBgColor          || undefined,
		'--mwsb-wl-card-btn-icon':         wlBtnIconColor        || undefined,
		'--mwsb-wl-card-btn-hover-bg':     wlBtnHoverBgColor     || undefined,
		'--mwsb-wl-card-btn-hover-icon':   wlBtnHoverIconColor   || undefined,
		'--mwsb-wl-card-btn-hover-border': wlBtnHoverBorderColor || undefined,
		'--mwsb-wl-card-btn-border-color': wlBtnBorderColor      || undefined,
		'--mwsb-wl-card-btn-border-width': wlBtnBorderWidth > 0 ? `${ wlBtnBorderWidth }px` : undefined,
		'--mwsb-wl-card-btn-radius':       wlBtnBorderRadius != null ? `${ wlBtnBorderRadius }px` : undefined,
		'--mwsb-wl-card-btn-pad-top':      wlBtnPadding?.top    || undefined,
		'--mwsb-wl-card-btn-pad-right':    wlBtnPadding?.right  || undefined,
		'--mwsb-wl-card-btn-pad-bottom':   wlBtnPadding?.bottom || undefined,
		'--mwsb-wl-card-btn-pad-left':     wlBtnPadding?.left   || undefined,
		'--mwsb-wl-card-btn-active-color': wlBtnActiveColor     || undefined,
		'--mwsb-wl-card-btn-active-bg':    wlBtnActiveBgColor   || undefined,
	};

	const qvBtnStyle = {
		'--mwsb-qv-card-btn-bg':           qvBtnBgColor          || undefined,
		'--mwsb-qv-card-btn-text':         qvBtnTextColor        || undefined,
		'--mwsb-qv-card-btn-hover-bg':     qvBtnHoverBgColor     || undefined,
		'--mwsb-qv-card-btn-hover-text':   qvBtnHoverTextColor   || undefined,
		'--mwsb-qv-card-btn-hover-border': qvBtnHoverBorderColor || undefined,
		'--mwsb-qv-card-btn-border-color': qvBtnBorderColor      || undefined,
		'--mwsb-qv-card-btn-border-width': qvBtnBorderWidth > 0 ? `${ qvBtnBorderWidth }px` : undefined,
		'--mwsb-qv-card-btn-radius':       qvBtnBorderRadius != null ? `${ qvBtnBorderRadius }px` : undefined,
		'--mwsb-qv-card-btn-font-size':    qvBtnFontSize ? `${ qvBtnFontSize }px` : undefined,
		'--mwsb-qv-card-btn-pad-top':      qvBtnPadding?.top    || undefined,
		'--mwsb-qv-card-btn-pad-right':    qvBtnPadding?.right  || undefined,
		'--mwsb-qv-card-btn-pad-bottom':   qvBtnPadding?.bottom || undefined,
		'--mwsb-qv-card-btn-pad-left':     qvBtnPadding?.left   || undefined,
	};

	function handlePrev( e ) {
		e.preventDefault();
		setActiveImage( ( i ) => ( i - 1 + images.length ) % images.length );
	}

	function handleNext( e ) {
		e.preventDefault();
		setActiveImage( ( i ) => ( i + 1 ) % images.length );
	}

	function handleWishlist( e ) {
		e.preventDefault();
		setWishlisted( toggleWishlist( product.id ) );
	}

	async function handleAddToCart( e ) {
		e.preventDefault();
		if ( product.type !== 'simple' ) {
			window.location.href = product.permalink;
			return;
		}
		setAdding( true );
		try {
			const response = await fetch( `${ window.mwsbData?.ajaxUrl }`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
				body: new URLSearchParams( {
					action:     'woocommerce_add_to_cart',
					product_id: product.id,
					quantity:   1,
					security:   window.mwsbData?.nonce,
				} ),
			} );
			const data = await response.json();

			// Apply the fragments returned by WC directly to the DOM.
			// wc-add-to-cart.js normally does this but only initialises when
			// wc_add_to_cart_params is defined — WC only outputs that variable when
			// it detects standard product-loop markup, which our block pages lack.
			if ( window.jQuery && data.fragments ) {
				const $ = window.jQuery;
				$.each( data.fragments, function( selector, html ) {
					$( selector ).replaceWith( html );
				} );
				// Let cart-fragments.js persist the new hash to sessionStorage.
				$( document.body ).trigger( 'added_to_cart', [ data.fragments, data.cart_hash ] );
			}

			// The Mini Cart block drives its count badge via the 'wc/store/cart'
			// Redux store (wc.wcBlocksData).  Invalidating its resolution forces a
			// fresh fetch from the Store API; the store's subscriber in
			// mini-cart-frontend.js then calls w() which rewrites the badge in the
			// DOM.  Without this step the count never updates when cart changes
			// originate outside of WC Blocks' own add-to-cart flow.
			window.wp?.data
				?.dispatch( 'wc/store/cart' )
				?.invalidateResolutionForStore?.();

			// Fire the CustomEvent so the Mini Cart (Interactivity API) calls
			// refreshCartItems. The directive is data-wp-on-document-- which
			// attaches to `document`, not `document.body`, so the event MUST
			// bubble (default is bubbles:false which is why the badge never updated).
			document.body.dispatchEvent(
				new CustomEvent( 'wc-blocks_added_to_cart', { bubbles: true, detail: { productId: product.id } } )
			);

			setAdded( true );
			setTimeout( () => setAdded( false ), 2000 );
		} finally {
			setAdding( false );
		}
	}

	const cartButton = showAddToCart && (
		<button
			className={ classNames(
				'mwsb-btn mwsb-btn--cart',
				cartAsIcon && 'mwsb-btn--cart-icon',
				( cartOnImage || cartOnCard ) && 'mwsb-btn--cart-floating',
				adding && 'mwsb-btn--loading',
				outOfStock && 'mwsb-btn--disabled',
				added && 'mwsb-btn--added'
			) }
			style={ btnStyle }
			onClick={ handleAddToCart }
			disabled={ outOfStock || adding }
			aria-label={ cartAsIcon ? ( added ? __( 'Added', 'mwsb' ) : product.add_to_cart.text ) : undefined }
		>
			{ adding && <span className="mwsb-spinner" /> }
			{ ! adding && cartAsIcon && <CartIcon added={ added } plus={ product.type !== 'simple' } size={ cartIconSize || 20 } /> }
			{ ! adding && ! cartAsIcon && added && __( 'Added', 'mwsb' ) }
			{ ! adding && ! cartAsIcon && ! added && product.add_to_cart.text }
		</button>
	);

	return (
		<article
			className={ classNames(
				'mwsb-product-card',
				`mwsb-preset-${ layoutPreset }`,
				outOfStock && 'mwsb-out-of-stock',
				! isEditor && wlBtnShowOnHover && wlOnImage && 'mwsb-wl-hover-reveal'
			) }
			style={ cardStyle }
		>
			{ ( showImage || showImageSlider ) && images.length > 0 && (
				<div className={ classNames(
					'mwsb-product-card__image-wrap',
					wlOnImage && `mwsb-has-wl-${ wlBtnPosition.replace( 'image-', '' ) }`
				) }>
					{ isEditor ? (
						<div className="mwsb-product-card__image" style={ imageStyle }>
							<img
								src={ images[ activeImage ] }
								alt={ product.name }
								loading="lazy"
								width="300"
								height="300"
								style={ imgStyle }
							/>
						</div>
					) : (
						<a href={ product.permalink }>
							<div className="mwsb-product-card__image" style={ imageStyle }>
								<img
									src={ images[ activeImage ] }
									alt={ product.name }
									loading="lazy"
									width="300"
									height="300"
									style={ imgStyle }
								/>
							</div>
						</a>
					) }
					{ showSaleBadge && product.on_sale && (
						<span className="mwsb-badge mwsb-badge--sale" style={ saleBadgeStyle }>{ __( 'Sale', 'mwsb' ) }</span>
					) }
					{ showStockBadge && outOfStock && (
						<span className="mwsb-badge mwsb-badge--stock">{ __( 'Out of Stock', 'mwsb' ) }</span>
					) }
					{ showImageSlider && hasGallery && (
						<>
							<button className="mwsb-img-arrow mwsb-img-arrow--prev" onClick={ handlePrev } aria-label={ __( 'Previous image', 'mwsb' ) }>
								&#8249;
							</button>
							<button className="mwsb-img-arrow mwsb-img-arrow--next" onClick={ handleNext } aria-label={ __( 'Next image', 'mwsb' ) }>
								&#8250;
							</button>
						</>
					) }
					{ wlOnImage && (
						<button
							className={ classNames(
								'mwsb-btn', 'mwsb-btn--wishlist', 'mwsb-btn--wishlist-on-image',
								`mwsb-wl-pos-${ wlBtnPosition.replace( 'image-', '' ) }`,
								wishlisted && 'mwsb-btn--wishlist-active'
							) }
							style={ wlBtnStyle }
							onClick={ handleWishlist }
							aria-label={ wishlisted ? __( 'Remove from wishlist', 'mwsb' ) : __( 'Add to wishlist', 'mwsb' ) }
							aria-pressed={ wishlisted }
						>
							<svg viewBox="0 0 24 24" width="18" height="18">
								<path
									d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
									fill={ wishlisted ? 'currentColor' : 'none' }
									stroke="currentColor"
									strokeWidth={ wishlisted ? '0' : '2' }
								/>
							</svg>
						</button>
					) }
					{ cartOnImage && cartButton }
				</div>
			) }

			<div className="mwsb-product-card__body" style={ bodyStyle }>
				{ showCategories && !! product.categories?.length && categoryPosition !== 'after-title' && (
					<div className="mwsb-product-card__categories" style={ categoryStyle }>
						{ product.categories.map( ( c ) => c.name ).join( ', ' ) }
					</div>
				) }
				{ showTitle && (
					<h3 className="mwsb-product-card__title" style={ titleStyle }>
						{ ( isEditor || titleLink === false )
							? product.name
							: <a href={ product.permalink }>{ product.name }</a>
						}
					</h3>
				) }
				{ showCategories && !! product.categories?.length && categoryPosition === 'after-title' && (
					<div className="mwsb-product-card__categories" style={ categoryStyle }>
						{ product.categories.map( ( c ) => c.name ).join( ', ' ) }
					</div>
				) }

				{ showRating && product.rating.count > 0 && (
					<div className="mwsb-product-card__rating" aria-label={ sprintf( __( 'Rated %s out of 5', 'mwsb' ), product.rating.average ) }>
						<Stars rating={ product.rating.average } />
						<span className="mwsb-product-card__rating-count">({ product.rating.count })</span>
					</div>
				) }

				{ showPrice && (
					<div
						className="mwsb-product-card__price"
						style={ priceStyle }
						dangerouslySetInnerHTML={ { __html: product.price_html } }
					/>
				) }

				{ showDescription && product.short_description && (
					<p className="mwsb-product-card__desc">{ product.short_description }</p>
				) }

				<div className="mwsb-product-card__actions" style={ { justifyContent: alignJustify } }>
					{ ! cartOnImage && ! cartOnCard && cartButton }

					{ showQuickView && (
						<button
							className="mwsb-btn mwsb-btn--quick-view"
							style={ qvBtnStyle }
							data-product-id={ product.id }
							onClick={ () => document.dispatchEvent( new CustomEvent( 'mwsb:quick-view', { detail: product } ) ) }
						>
							{ __( 'Quick View', 'mwsb' ) }
						</button>
					) }

					{ showWishlist && ! wlOnImage && (
						<button
							className={ classNames( 'mwsb-btn mwsb-btn--wishlist', wishlisted && 'mwsb-btn--wishlist-active' ) }
							style={ wlBtnStyle }
							onClick={ handleWishlist }
							aria-label={ wishlisted ? __( 'Remove from wishlist', 'mwsb' ) : __( 'Add to wishlist', 'mwsb' ) }
							aria-pressed={ wishlisted }
						>
							<svg viewBox="0 0 24 24" width="18" height="18">
								<path
									d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
									fill={ wishlisted ? 'currentColor' : 'none' }
									stroke="currentColor"
									strokeWidth={ wishlisted ? '0' : '2' }
								/>
							</svg>
						</button>
					) }
				</div>
			</div>

			{ cartOnCard && cartButton }
		</article>
	);
}

function CartIcon( { added, plus, size } ) {
	return (
		<svg viewBox="0 0 24 24" width={ size } height={ size } fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
			{ added && <path d="M20 6L9 17l-5-5" /> }
			{ ! added && plus && <path d="M12 5v14M5 12h14" /> }
			{ ! added && ! plus && (
				<>
					<circle cx="9" cy="21" r="1" />
					<circle cx="20" cy="21" r="1" />
					<path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
				</>
			) }
		</svg>
	);
}

function Stars( { rating } ) {
	const full  = Math.floor( rating );
	const half  = rating - full >= 0.5 ? 1 : 0;
	const empty = 5 - full - half;

	return (
		<span className="mwsb-stars">
			{ Array.from( { length: full } ).map(  ( _, i ) => <Star key={ `f${ i }` } type="full" /> ) }
			{ half === 1 && <Star type="half" /> }
			{ Array.from( { length: empty } ).map( ( _, i ) => <Star key={ `e${ i }` } type="empty" /> ) }
		</span>
	);
}

function Star( { type } ) {
	const fill = type === 'full' ? '#f0c040' : type === 'half' ? 'url(#half)' : '#ddd';
	return (
		<svg className={ `mwsb-star mwsb-star--${ type }` } viewBox="0 0 24 24" width="14" height="14">
			{ type === 'half' && (
				<defs>
					<linearGradient id="half">
						<stop offset="50%" stopColor="#f0c040" />
						<stop offset="50%" stopColor="#ddd" />
					</linearGradient>
				</defs>
			) }
			<path
				d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
				fill={ fill }
			/>
		</svg>
	);
}
